import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { MoodLevel } from '../types';
import { getTimeOfDayLabel, getMoodEmoji } from '../utils/moodUtils';
import { isDateToday } from '../utils/dateUtils';

interface MoodSelectorProps {
  timeOfDay: 'morning' | 'afternoon' | 'evening';
  date: Date;
  onSelect: (mood: MoodLevel, notes?: string) => void;
  onClose: () => void;
}

const MoodSelector: React.FC<MoodSelectorProps> = ({ timeOfDay, date, onSelect, onClose }) => {
  const [selectedMood, setSelectedMood] = useState<MoodLevel | null>(null);
  const [notes, setNotes] = useState('');
  const [showNotesInput, setShowNotesInput] = useState(false);
  const timeLabel = getTimeOfDayLabel(timeOfDay);
  const isToday = isDateToday(date);
  const reflectionPerPeriod = localStorage.getItem('reflectionPerPeriod') === 'true';
  
  const handleMoodSelect = (mood: MoodLevel) => {
    setSelectedMood(mood);
    if (reflectionPerPeriod) {
      setShowNotesInput(true);
    } else {
      onSelect(mood);
      onClose();
    }
  };

  const handleSubmit = () => {
    if (selectedMood) {
      onSelect(selectedMood, notes || undefined);
      onClose();
    }
  };

  const handleSkip = () => {
    if (selectedMood) {
      onSelect(selectedMood);
      onClose();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-xl shadow-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {isToday 
              ? `How are you getting on this ${timeLabel.toLowerCase()}?`
              : `How did it go for you that ${timeLabel.toLowerCase()}?`}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        {!showNotesInput ? (
          <div className="grid grid-cols-5 gap-3">
            {([1, 2, 3, 4, 5] as MoodLevel[]).map((mood) => (
              <motion.button
                key={mood}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center justify-center p-3 rounded-lg transition-colors"
                onClick={() => handleMoodSelect(mood)}
              >
                <span className="text-6xl">{getMoodEmoji(mood)}</span>
              </motion.button>
            ))}
          </div>
        ) : (
          <div>
            <div className="text-center mb-4">
              <span className="text-6xl">{selectedMood && getMoodEmoji(selectedMood)}</span>
            </div>
            
            <p className="text-gray-600 mb-4">
              Add any notes about your {timeLabel.toLowerCase()} (optional):
            </p>
            
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors mb-4"
              rows={3}
              placeholder={`Notes about my ${timeLabel.toLowerCase()}...`}
            />
            
            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors flex-1"
              >
                Skip Notes
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex-1"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default MoodSelector;